import React, { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import AcUnitIcon from '@mui/icons-material/AcUnit'
import CelebrationIcon from '@mui/icons-material/Celebration'
import apiClient from '../../../services/apiClient'
import AlertBox from '../../alert/AlertBox'
import logo from '../../../asset/images/logo.png'

function Concours () {
  const [form, setForm] = useState({ nom: '', adresse: '', courriel: '', telephone: '' })
  const [acceptePromotions, setAcceptePromotions] = useState(false)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [serverError, setServerError] = useState(false)

  const validate = () => {
    const newErrors = {}
    if (!form.nom.trim()) newErrors.nom = 'Le nom est obligatoire.'
    if (!form.adresse.trim()) newErrors.adresse = "L'adresse est obligatoire."
    if (!form.courriel.trim()) {
      newErrors.courriel = 'Le courriel est obligatoire.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.courriel)) {
      newErrors.courriel = 'Le courriel est invalide.'
    }
    if (!form.telephone.trim()) newErrors.telephone = 'Le numéro de téléphone est obligatoire.'
    if (!acceptePromotions) newErrors.acceptePromotions = "Vous devez autoriser l'envoi de promotions pour participer au concours."
    return newErrors
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setErrors({ ...errors, [e.target.name]: undefined })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    setLoading(true)
    setServerError(false)
    try {
      await apiClient.inscrireAuConcours(form)
      setSuccess(true)
    } catch {
      setServerError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth='md' sx={{ py: 6 }}>
      <Grid container spacing={3} justifyContent='center'>

        {/* Logo */}
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Box component='img' src={logo} alt='DMJ Déneigement' sx={{ maxHeight: 120, maxWidth: '80%' }} />
        </Grid>

        {/* Titre */}
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Typography variant='h4' fontWeight='bold'>
            <AcUnitIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#00aeee' }} />
            Concours Déneigement Gratuit – Saison 2026-2027
            <AcUnitIcon sx={{ verticalAlign: 'middle', ml: 1, color: '#00aeee' }} />
          </Typography>
        </Grid>

        {/* Intro */}
        <Grid item xs={12}>
          <Typography variant='body1' sx={{ textAlign: 'center', mb: 1 }}>
            Inscrivez-vous et courez la chance de gagner le déneigement <strong>GRATUIT</strong> de votre entrée pour toute la saison 2026-2027!
          </Typography>
        </Grid>

        {/* Tirage */}
        <Grid item xs={12}>
          <Box sx={{ backgroundColor: '#f0f8ff', borderRadius: 2, p: 3, textAlign: 'center' }}>
            <Typography variant='body1' fontWeight='bold'>
              <CelebrationIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#00aeee' }} />
              Un gagnant est tiré à chaque tranche de 20 inscriptions!
            </Typography>
            <Typography variant='body1'>
              Plus il y a de participants, plus il y a de chances de gagner!
            </Typography>
          </Box>
        </Grid>

        {/* Comment participer */}
        <Grid item xs={12}>
          <Typography variant='h5' fontWeight='bold' sx={{ mb: 1 }}>Comment participer ?</Typography>
          <Typography variant='body1' sx={{ mb: 1 }}>C'est simple et rapide :</Typography>
          <Typography variant='body1'>
            👉 Remplissez le formulaire d'inscription en entrant :
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant='body1'>✔ Votre nom</Typography>
            <Typography variant='body1'>✔ Votre adresse</Typography>
            <Typography variant='body1'>✔ Votre courriel</Typography>
            <Typography variant='body1'>✔ Votre numéro de téléphone</Typography>
          </Box>
          <Typography variant='body1' sx={{ mt: 1 }}>
            Et voilà, vous êtes automatiquement inscrit au tirage!
          </Typography>
        </Grid>

        {/* Détails */}
        <Grid item xs={12}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant='h5' fontWeight='bold' sx={{ mb: 1 }}>📢 Détails du concours</Typography>
          <Box>
            <Typography variant='body1'>✔ Tirage effectué à chaque groupe de 20 inscriptions</Typography>
            <Typography variant='body1'>✔ Le gagnant remporte le déneigement de son entrée pour la saison 2026-2027</Typography>
            <Typography variant='body1'>✔ Les gagnants seront contactés directement</Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Formulaire */}
        {success
          ? (
            <Grid item xs={12}>
              <AlertBox
                type='success'
                message="Félicitations! Votre inscription a été enregistrée. Bonne chance! ❄️"
              />
            </Grid>
            )
          : (
            <Grid item xs={12} component='form' onSubmit={handleSubmit}>
              <Typography variant='h5' fontWeight='bold' sx={{ mb: 2 }}>
                Inscrivez-vous dès maintenant!
              </Typography>

              {serverError && (
                <Box sx={{ mb: 2 }}>
                  <AlertBox
                    type='error'
                    message="Une erreur s'est produite. Veuillez réessayer plus tard."
                  />
                </Box>
              )}

              <TextField
                fullWidth
                label='Nom complet'
                name='nom'
                value={form.nom}
                onChange={handleChange}
                error={!!errors.nom}
                helperText={errors.nom}
                required
              />
              <TextField
                fullWidth
                label='Adresse'
                name='adresse'
                value={form.adresse}
                onChange={handleChange}
                error={!!errors.adresse}
                helperText={errors.adresse}
                required
              />
              <TextField
                fullWidth
                label='Courriel'
                name='courriel'
                type='email'
                value={form.courriel}
                onChange={handleChange}
                error={!!errors.courriel}
                helperText={errors.courriel}
                required
              />
              <TextField
                fullWidth
                label='Numéro de téléphone'
                name='telephone'
                value={form.telephone}
                onChange={handleChange}
                error={!!errors.telephone}
                helperText={errors.telephone}
                required
              />

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptePromotions}
                      onChange={(e) => {
                        setAcceptePromotions(e.target.checked)
                        setErrors({ ...errors, acceptePromotions: undefined })
                      }}
                    />
                  }
                  label="J'autorise Déneigement MJ à m'envoyer des promotions par courriel, texto ou téléphone."
                />
              </Box>

              {errors.acceptePromotions && (
                <Box sx={{ mt: 2 }}>
                  <AlertBox
                    type='error'
                    message={errors.acceptePromotions}
                  />
                </Box>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  type='submit'
                  variant='contained'
                  size='large'
                  disabled={loading}
                  sx={{ px: 6 }}
                >
                  {loading ? <CircularProgress size={24} /> : "M'inscrire au concours ❄️"}
                </Button>
              </Box>
            </Grid>
            )}

        {/* Footer */}
        <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Ne manquez pas votre chance de passer l'hiver sans pelle ni stress! ❄️
          </Typography>
        </Grid>

      </Grid>
    </Container>
  )
}

export default Concours
